import type {AxiosError} from 'axios';
import axios from 'axios';
import type {DeliveryDistanceResults, LocationData, TenantLocation} from '@/types';
import type {StoreLoaders} from '@/types/common';
import type {
    DistanceUnit,
    ProductOrder,
    ProductOrderCosts,
    ProductOrderCustomer,
    ProductOrderDates,
    ProductOrderDuration,
    ProductOrderExtraV2
} from '@/types/order';
import type {Module} from 'vuex'
import * as types from '@/store/mutation-types';
import type {RootState} from '@/types/RootState';
import {CartService} from '@/services/cart.service';
import {Form} from '@/plugins/FWForms/index';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import router from '@/router';
import {d} from '@/utils/consoleHelper.ts';
import {fmtDateString} from '@/utils/dates.ts';
import dayjs from 'dayjs';
import type {TripExtraOption} from '@/types/trip-extra.ts';


export interface TenantCheckoutState {
    loading: StoreLoaders,
    dialogs: Record<string, boolean>,
    productOrder: ProductOrder,
    showDeliveryAddressForm: boolean,
    stripePaymentIntent: any,
    tenantCheckoutForm: Form<any>,
    deliveryDistanceResults: DeliveryDistanceResults,
}

export interface CheckDrivingDistanceParams {
    location: LocationData;
    company_uuid: string;
    delivery: string;
}


const state: TenantCheckoutState = {
    loading: {
        checkout: false,
        stripe_oauth: false,
        createApiToken: false,
        start2FAVerify: false,
        check2FAVerify: false,
    },

    dialogs: {
        stripePayment: false,
    },

    stripePaymentIntent: null,

    productOrder: {
        version: 'v2',
        l_id: '',
        b_id: '',
        p_id: '',
        dates: {
            start: fmtDateString(dayjs().add(2, 'day')),
            end: fmtDateString(dayjs().add(3, 'day')),
        },
        costs: {
            vehicle: 0,
            extras: 0,
            requiredExtras: 0,
            delivery: 0,
            taxes: 0,
            discount: 0,
            subtotal: 0,
            grandTotal: 0,
            dueToday: 0,
            securityDepositCredit: 0,
            securityDepositDebit: 0,
        },
        discounts: {
            discounted: false,
            amount: 0,
            value: 0,
            code: null,
            reason: null,
            type: null,
            durationDiscountReason: null,
            durationDiscountAmount: null,
            durationDiscountValue: null,
        },
        duration: {
            seconds: 0,
            minutes: 0,
            hours: 0,
            days: 0,
            weeks: 0,
            months: 0,
            years: 0,
            units: 1,
        },
        customers: [
            {
                isPrimary: true,
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                dob: '',
                age: null,
                signaturePng: null,
                signFullName: null,
                signInitials: null,
                agreeAdoptSignature: false,
                agreeToS: false,
            },
        ],
        extras: [],
        extrasV2: [],
        requiredExtras: [],
        requiredExtrasV2: {},
        delivery: null,
        delivery_address: null,
        distance_allowance: {
            distance: null,
            unit: 'mile',
        },
        customFields: [],
        cartId: CartService.getCartUuid(),
    },

    tenantCheckoutForm: new Form(),

    showDeliveryAddressForm: false,
    deliveryDistanceResults: {
        checked: false as boolean,
        valid: false as boolean,
        message: null as string | null,
        distance: null as number | null,
    },
}


const tenantCheckoutModule: Module<TenantCheckoutState, RootState> = {
    namespaced: true,

    state,

    getters: {
        loading: (state: TenantCheckoutState) => state.loading,
        dialogs: (state: TenantCheckoutState) => state.dialogs,
        stripePaymentIntent: (state: TenantCheckoutState) => state.stripePaymentIntent,
        productOrder: (state: TenantCheckoutState) => state.productOrder,
        showDeliveryAddressForm: (state: TenantCheckoutState) => state.showDeliveryAddressForm,
        deliveryDistanceResults: (state: TenantCheckoutState) => state.deliveryDistanceResults,
        tenantCheckoutForm: (state: TenantCheckoutState) => state.tenantCheckoutForm,
    },


    mutations: {
        [types.SET_CHECKOUT_LOADING](state: TenantCheckoutState, loading: { subject: string, loading: boolean }) {
            state.loading[loading.subject] = loading.loading;
        },
        [types.SET_TENANT_CHECKOUT_DATES](state: TenantCheckoutState, dates: ProductOrderDates) {
            d.log('%cSET_TENANT_CHECKOUT_DATES', 'color: red; font-weight: bold;');
            state.productOrder.dates = dates;
        },
        [types.SET_TENANT_CHECKOUT_LOCATION](state, location: TenantLocation | null) {
            d.log('%cSET_TENANT_CHECKOUT_LOCATION', 'color: pink; font-weight: bold;');
            state.productOrder.delivery = location;
        },
        [types.SET_TENANT_CHECKOUT_DELIVERY_ADDRESS_FORM_SHOW](state: TenantCheckoutState, payload) {
            state.showDeliveryAddressForm = payload;
        },
        [types.SET_TENANT_CHECKOUT_STRIPE_PAYMENT_INTENT](state: TenantCheckoutState, payload) {
            state.stripePaymentIntent = payload;
        },
        [types.SET_TENANT_CHECKOUT_DIALOG](state: TenantCheckoutState, payload) {
            if (payload.topic === 'stripe-payment') {
                state.dialogs.stripePayment = payload.value;
            }
        },
        [types.SET_TENANT_CHECKOUT_DURATIONS](state: TenantCheckoutState, payload: ProductOrderDuration) {
            state.productOrder.duration = payload;
        },
        [types.SET_TENANT_CHECKOUT_BOOKING_ID](state: TenantCheckoutState, payload: string) {
            state.productOrder.b_id = payload;
        },
        [types.SET_TENANT_CHECKOUT_CUSTOM_ORDER_FIELDS](state: TenantCheckoutState, payload) {
            state.productOrder.customFields = payload;
        },
        [types.SET_TENANT_CHECKOUT_CUSTOMER_TOS_AGREE](state: TenantCheckoutState, data: {
            index: number,
            agreeToS: boolean
        }) {
            state.productOrder.customers[data.index].agreeToS = data.agreeToS;
        },
        [types.SET_TENANT_CHECKOUT_COSTS](state: TenantCheckoutState, payload: ProductOrderCosts) {
            state.productOrder.costs = payload;
        },
        [types.SET_TENANT_CHECKOUT_LISTING_ID](state: TenantCheckoutState, payload: string) {
            state.productOrder.l_id = payload;
        },
        [types.SET_TENANT_CHECKOUT_CUSTOMER_BY_INDEX](
            state,
            data: { index: number, customer: ProductOrderCustomer }
        ) {
            state.productOrder.customers[data.index] = data.customer;
        },
        [types.SET_TENANT_CHECKOUT_CUSTOMERS](state: TenantCheckoutState, payload: ProductOrderCustomer[]) {
            state.productOrder.customers = payload;
        },
        [types.SET_TENANT_CHECKOUT_EXTRAS_V2](state: TenantCheckoutState, payload: ProductOrderExtraV2[]) {
            d.dir('SET_TENANT_CHECKOUT_EXTRAS_V2');
            state.productOrder.extrasV2 = payload;
        },
        [types.SET_TENANT_CHECKOUT_REQUIRED_EXTRAS_V2](
            state,
            payload: {
                index: string,
                extra: ProductOrderExtraV2,
                choice: TripExtraOption
            }
        ) {
            d.dir('SET_TENANT_CHECKOUT_REQUIRED_EXTRAS_V2 !@!@!@')
            d.dir(payload)
            if (!state.productOrder.requiredExtrasV2) {
                state.productOrder.requiredExtrasV2 = {};
            }

            state.productOrder.requiredExtrasV2[payload.index] = {
                ...payload.extra,
                choice: payload.choice,
            };
        },

        [types.SET_TENANT_CHECKOUT_REQUIRED_EXTRAS](state: TenantCheckoutState, data) {
            if (data.requiredExtras) {
                state.productOrder.requiredExtras = data.requiredExtras.map(e => e.extras[0]);
            }

            if (data.requiredExtrasV2) {
                state.productOrder.requiredExtrasV2 = data.requiredExtrasV2.map(e => {
                    return {
                        parentUuid: e.uuid,
                        choice: e.options[0],
                    };
                });
            }
        },
        [types.SET_TENANT_CHECKOUT_ORDER_DELIVERY_ADDRESS](state: TenantCheckoutState, address: LocationData) {
            state.productOrder.delivery_address = address;
        },
        [types.SET_TENANT_CHECKOUT_ORDER_DISTANCE_ALLOWANCE](state: TenantCheckoutState, payload: {
            distance: number,
            unit: DistanceUnit
        }) {
            state.productOrder.distance_allowance = payload;
        },
        [types.RESET_TENANT_CHECKOUT_DELIVERY_RESULTS](state: TenantCheckoutState) {
            state.deliveryDistanceResults = {
                checked: false,
                valid: false,
                message: null,
                distance: null,
            };
        },
        [types.SET_TENANT_CHECKOUT_DELIVERY_RESULTS](state: TenantCheckoutState, payload: DeliveryDistanceResults) {
            state.deliveryDistanceResults = payload;
        },
        [types.SET_TENANT_CHECKOUT_DISCOUNT](state, coupon) {
            d.dir(coupon);

            state.productOrder.discounts = {
                discounted: true,
                value: coupon?.value,
                code: coupon?.code,
                amount: coupon?.amount,
                reason: coupon?.reason,
                type: coupon?.type,
                durationDiscountReason: null,
                durationDiscountAmount: null,
                durationDiscountValue: null,
            };
        },
        [types.RESET_TENANT_CHECKOUT_DISCOUNT](state) {
            state.tenantCheckoutForm.errors.clear('coupon');
            state.productOrder.discounts = {
                discounted: false,
                code: '',
                amount: 0,
                reason: '',
                type: '',
                value: 0,
                durationDiscountReason: null,
                durationDiscountAmount: null,
                durationDiscountValue: null,
            };
        },
        [types.SET_TENANT_CHECKOUT_FORM_ERRORS](state, errors) {
            state.tenantCheckoutForm.errors.set(errors);
        },
        [types.RESET_TENANT_CHECKOUT_FORM_ERRORS](state) {
            state.tenantCheckoutForm.errors.clear();
        }
    },

    actions: {
        setLocation({commit}, location: TenantLocation | null) {
            commit(types.SET_TENANT_CHECKOUT_LOCATION, location);
        },
        setCustomOrderCheckoutFields({commit}, fields) {
            commit(types.SET_TENANT_CHECKOUT_CUSTOM_ORDER_FIELDS, fields);
        },
        resetDeliveryDistanceResults({commit}) {
            commit(types.RESET_TENANT_CHECKOUT_DELIVERY_RESULTS);
        },
        setDeliveryAddressFormVisibility({commit}, payload: boolean) {
            commit(types.SET_TENANT_CHECKOUT_DELIVERY_ADDRESS_FORM_SHOW, payload);
        },
        setDeliveryDistanceResults({state}, payload) {
            state.deliveryDistanceResults = payload;
        },
        /**
         * Check the distance between the delivery location and the listing
         */
        async checkDrivingDistance({commit}, location: CheckDrivingDistanceParams) {
            try {
                await performApiRequest(commit, 'SET_CHECKOUT_LOADING', 'checkout', () => axios.post('/v1/verify-driving-distance', location));
                commit(types.SET_TENANT_CHECKOUT_DELIVERY_RESULTS, {
                    checked: true,
                    valid: true,
                });
            } catch (e: any) {
                commit(types.SET_TENANT_CHECKOUT_DELIVERY_RESULTS, {
                    checked: true,
                    valid: false,
                    message: e.response?.data?.message || 'There was an error checking the driving distance.',
                });
            }
        },
        async verifyPayment({commit, rootGetters}, paymentIntentId: string) {
            try {
                return await performApiRequest(commit, 'SET_CHECKOUT_LOADING', 'checkout', () => axios.get(`/v1/company/${rootGetters['company/company'].uuid}/verify-payment-intent/${paymentIntentId}`));
            } catch (error) {
                d.error('error');
                d.dir(error);
            }
        },
        async tenantCheckout({commit, rootGetters, state}) {
            try {
                const paymentProvider = rootGetters['company/company']?.payment_profile?.provider;
                const notingDueToday = state.productOrder.costs.dueToday === 0;

                const responseData = await performApiRequest(commit, 'SET_CHECKOUT_LOADING', 'checkout', () => axios.post('/v1/checkout', state.productOrder));

                // if there is no payment provider, redirect to the success page
                if (paymentProvider === null || (paymentProvider !== 'square' && paymentProvider !== 'stripe') || notingDueToday) {
                    // send it to the checkout success page
                    router.push({
                        name: 'reservations.complete',
                        query: {
                            b_id: responseData?.b_id,
                            l_id: responseData?.l_id,
                            company: rootGetters['company/company']?.slug
                        }
                    }).catch(() => {
                    });
                    return;
                }

                // If the payment provider is Square, redirect to Square payment page
                if (paymentProvider === 'square') {
                    window.location.href = responseData?.redirect;
                }

                if (responseData?.redirect) {
                    window.location.href = responseData?.redirect;
                    return;
                }


                // if the payment provider is Stripe
                if (paymentProvider === 'stripe') {
                    const stripePaymentMethod = 'payment_intent';

                    if (stripePaymentMethod === 'payment_intent') {
                        commit(types.SET_TENANT_CHECKOUT_BOOKING_ID, responseData?.b_id);
                        commit(types.SET_TENANT_CHECKOUT_STRIPE_PAYMENT_INTENT, responseData?.StripePaymentIntent);
                        commit(types.SET_TENANT_CHECKOUT_DIALOG, {
                            topic: 'stripe-payment',
                            value: true,
                        });
                    }
                }

                // If the payment provider is Stripe, redirect to the Stripe payment page

                d.dir(responseData);

            } catch (error) {
                const err = error as AxiosError;
                const errors = (err?.response?.data as Record<string, string[]>)?.errors;
                commit(types.SET_TENANT_CHECKOUT_FORM_ERRORS, errors);
            }
        }
    },

}

export default tenantCheckoutModule;